import * as React from 'react';
import { Link } from 'react-router-dom';
import { CSSTransitionGroup } from 'react-transition-group';
import { SignUpBox } from './SignUpBox';
import { SignUpBenefits } from './SignUpBenefits';
import { Advert } from '../Advert';
import { AsyncLoader } from '../Common/AsyncLoader';
import { FixedAspect } from '../Common/FixedAspect';
import { FixedAspectImage } from '../Common/FixedAspectImage';
import { LoadFailure } from '../Common/LoadFailure';
import { LoadingSpinner } from '../Common/LoadingSpinner';
import { SVG } from '../Utils/SVG';
import { VideoMiniList } from '../Video/VideoMiniList';
import { VideoDetailsBar } from '../Video/VideoDetailsBar';
import { AdvertSlot } from '../../constants';
import { SiteAdverts, Video } from '../../models';
import { VideoStore } from '../../stores';
const playIconURL = require('../../icons/play.svg');
import './style.css';

export interface HomeProps {
  adverts: SiteAdverts;
  featuredVideos: Array<Video>;
  featuredVideosError: boolean;
  featuredVideosLoading: boolean;
  loggedIn: boolean;
  pageUrl: string;
  recentlyAdded: Array<Video>;
  recentlyAddedError: boolean;
  recentlyAddedLoading: boolean;
  savingFavourite: boolean;
  videoError: boolean;
  videoLoading: boolean;
  onFavourite: (video: Video) => void;
}

export interface HomeState {
  activeFeaturedVideo?: Video;
}

export const FEATURED_VIDEO_CAROUSEL_DURATION = 5000;

export class Home extends React.Component<HomeProps, HomeState> {
  private videoCarouselInterval?: number;

  constructor(props: HomeProps) {
    super(props);
    this.state = {};
  }

  UNSAFE_componentWillMount() {
    // Try to start the carousel on mount - this happens when revisiting the homepage
    // since all of its data is already loaded (so no subsequent prop updates occur)
    this.tryToStartCarousel();
  }

  UNSAFE_componentWillReceiveProps(newProps: HomeProps) {
    // A prop change could indicate our data is ready, so try to start the carousel
    this.tryToStartCarousel(newProps);
  }

  componentWillUnmount() {
    if (this.videoCarouselInterval) {
      clearInterval(this.videoCarouselInterval);
      this.videoCarouselInterval = undefined;
    }
  }

  render() {
    const {
      adverts,
      featuredVideos,
      featuredVideosError,
      featuredVideosLoading,
      loggedIn,
      onFavourite,
      pageUrl,
      recentlyAdded,
      recentlyAddedError,
      recentlyAddedLoading,
      savingFavourite,
      videoError
    } = this.props;
    const { activeFeaturedVideo } = this.state;
    const { next, previous } = VideoStore.getNextAndPreviousFeaturedVideos(
      featuredVideos,
      activeFeaturedVideo
    );

    const videoIsStillLoading = !this.isSuccessfullyLoaded(this.props);

    return (
      <main className="home" role="main">
        <div className="video-area-wrapper">
          <Link
            className="featured-videos-carousel"
            to={
              activeFeaturedVideo
                ? Video.getFeaturedRoute(activeFeaturedVideo)
                : '/'
            }
          >
            <CSSTransitionGroup
              className="featured-videos-carousel-wrapper"
              component="div"
              transitionName="featured-videos-carousel-change"
              transitionEnterTimeout={500}
              transitionLeaveTimeout={500}
            >
              <div key={activeFeaturedVideo ? activeFeaturedVideo.id : -1}>
                <AsyncLoader
                  className="featured-videos-carousel-loader"
                  loaderFactory={(): JSX.Element => (
                    <FixedAspect>
                      <LoadingSpinner />
                    </FixedAspect>
                  )}
                  error={videoError}
                  errorMessageFactory={(errorClasses: string): JSX.Element => (
                    <FixedAspect className="featured-videos-carousel-error-fixed-aspect">
                      <LoadFailure
                        message="Sorry, an error occurred while trying to load the video."
                        className={errorClasses}
                        pageUrl={pageUrl}
                      />
                    </FixedAspect>
                  )}
                  loading={videoIsStillLoading}
                  pageUrl={pageUrl}
                >
                  {activeFeaturedVideo ? (
                    <FixedAspectImage
                      className="featured-videos-carousel-image"
                      loader={FixedAspectImage.defaultLoader}
                      src={activeFeaturedVideo.poster}
                    >
                      <div className="featured-videos-carousel-play-overlay">
                        <SVG
                          className="featured-videos-play-icon"
                          src={playIconURL}
                        />
                      </div>
                    </FixedAspectImage>
                  ) : null}
                </AsyncLoader>
              </div>
            </CSSTransitionGroup>
            {activeFeaturedVideo ? (
              <div className="featured-videos-carousel-play-overlay">
                <SVG className="featured-videos-play-icon" src={playIconURL} />
              </div>
            ) : null}
          </Link>
          {loggedIn ? (
            <CSSTransitionGroup
              className="featured-videos-details-wrapper"
              transitionName="featured-videos-carousel-change"
              transitionEnterTimeout={500}
              transitionLeaveTimeout={500}
            >
              <div key={activeFeaturedVideo ? activeFeaturedVideo.id : -1}>
                <AsyncLoader
                  className="featured-videos-carousel-details-loader video-details-bar"
                  dontShowError={true}
                  dontShowLoader={true}
                  error={videoError}
                  loading={videoIsStillLoading}
                  pageUrl={pageUrl}
                >
                  {activeFeaturedVideo ? (
                    <VideoDetailsBar
                      getVideoUrl={Video.getFeaturedRoute}
                      loggedIn={loggedIn}
                      nextVideo={next}
                      onFavourite={() => onFavourite(activeFeaturedVideo)}
                      previousVideo={previous}
                      savingFavourite={savingFavourite}
                      video={activeFeaturedVideo}
                    />
                  ) : null}
                </AsyncLoader>
              </div>
            </CSSTransitionGroup>
          ) : (
            <div className="sign-up-box-wrapper">
              <SignUpBox />
            </div>
          )}
        </div>
        <VideoMiniList
          error={recentlyAddedError}
          getVideoUrl={Video.getRoute}
          loading={recentlyAddedLoading}
          pageUrl={pageUrl}
          videos={recentlyAdded.slice(0, 3)}
          title="Recently Added Videos"
        />
        {adverts[AdvertSlot.HomepageBanner] ? (
          <div className="homepage-banner-advert-wrapper">
            <Advert
              className="homepage-banner-advert"
              adverts={adverts[AdvertSlot.HomepageBanner]}
            />
          </div>
        ) : (
          <div className="breakout-to-full-width divider-bar" />
        )}
        <VideoMiniList
          error={featuredVideosError}
          getVideoUrl={Video.getFeaturedRoute}
          loading={featuredVideosLoading}
          pageUrl={pageUrl}
          videos={featuredVideos.slice(1, 4)}
          title="Featured Videos"
        />
        <div className="breakout-to-full-width divider-bar divider-bar-reversed" />
        {loggedIn ? null : (
          <div className="sign-up-benefits-wrapper">
            <SignUpBenefits />
          </div>
        )}
      </main>
    );
  }

  private moveToNextFeaturedVideo = () => {
    const { featuredVideos } = this.props;

    if (!featuredVideos) {
      this.setState({
        activeFeaturedVideo: undefined
      });

      return;
    }

    const currentIndex = featuredVideos.indexOf(this.state
      .activeFeaturedVideo as Video);
    const index =
      currentIndex + 1 >= featuredVideos.length ? 0 : currentIndex + 1;

    this.setState({
      activeFeaturedVideo: featuredVideos[index]
    });
  };

  private tryToStartCarousel = (props: HomeProps = this.props) => {
    if (
      !this.videoCarouselInterval &&
      this.isSuccessfullyLoaded(props) &&
      this.hasFeaturedVideos(props)
    ) {
      this.startCarousel();
    }
  };

  private isSuccessfullyLoaded = (props: HomeProps): boolean => {
    const {
      featuredVideosError,
      featuredVideosLoading,
      recentlyAddedError,
      recentlyAddedLoading,
      videoError,
      videoLoading
    } = props;

    return !(
      featuredVideosError ||
      featuredVideosLoading ||
      recentlyAddedError ||
      recentlyAddedLoading ||
      videoError ||
      videoLoading
    );
  };

  private hasFeaturedVideos = (props: HomeProps): boolean => {
    const { featuredVideos } = props;

    return !!featuredVideos && !!featuredVideos.length;
  };

  private startCarousel = () => {
    // Move to the first featured video
    this.moveToNextFeaturedVideo();

    // Then setup an interval to autoplay through video posters
    this.videoCarouselInterval = window.setInterval(() => {
      if (this.videoCarouselInterval) {
        this.moveToNextFeaturedVideo();
      }
    }, FEATURED_VIDEO_CAROUSEL_DURATION);
  };
}



// WEBPACK FOOTER //
// ./src/app/components/Home/index.tsx